import AppLayout from "@/components/layouts/AppLayout";
import { usePage, Link, router } from "@inertiajs/react";
import { useState, type ReactNode } from "react";
import InertiaSharedProps from "@/types/inertia";
import Event from "@/types/serializers/Event";
import Preference from "@/types/serializers/Preference";
import { SEOHead } from "@/components/SEOHead";
import {
  formatTimeRange,
  calculateDuration,
  getDurationMinutes,
  createGapCalculator,
  PIXELS_PER_MINUTE,
} from "./utils";
import { Box, Flex, Heading, Text, VStack, Button, IconButton } from "@chakra-ui/react";
import { integrations_path, event_path } from "@/routes";
import { LuX } from "react-icons/lu";

interface DateWithEvents {
  date: string;
  label: string;
  events: Event[];
}

interface DashboardProps {
  has_integrations: boolean;
  preference: Preference;
  dates: DateWithEvents[];
}

const Index = ({ has_integrations, preference, dates }: DashboardProps) => {
  const { current_user } = usePage<InertiaSharedProps>().props;
  const [selectedIndex, setSelectedIndex] = useState(0);

  const { label: selectedLabel, events: selectedEvents } = dates[selectedIndex];
  const calculateGapPixels = createGapCalculator(preference.day_start_time_hhmm, preference.day_end_time_hhmm);

  const handleDeleteEvent = (eventId: number) => {
    if (confirm("Remove this focus time? Your schedule will be recalculated.")) {
      router.delete(event_path(eventId));
    }
  };

  return (
    <Box>
      <SEOHead
        title="Dashboard - Muro"
        description="Manage your automated focus time blocks and deep work sessions."
        noindex={true}
      />
      <Flex gap={2} mb={6}>
        {dates.map((tab, index) => (
          <Button
            key={tab.date}
            onClick={() => setSelectedIndex(index)}
            variant={selectedIndex === index ? "solid" : "outline"}
            colorPalette="primary"
            size="sm"
          >
            {tab.label}
          </Button>
        ))}
      </Flex>
      <Box mb={12}>
        <Heading as="h1" size="xl" mb={2} color="primary-bold">
          {selectedLabel}
        </Heading>
        {has_integrations && (
          <Text color="gray.600" mb={6}>
            Hey {current_user?.first_name}, your day looks like this
          </Text>
        )}
      </Box>

      {!has_integrations && (
        <Box
          textAlign="center"
          py={12}
          px={6}
          borderRadius="lg"
          borderWidth="2px"
          borderColor="blue.200"
          bg="blue.50"
          maxW="2xl"
          mx="auto"
        >
          <Heading as="h2" size="lg" mb={4} color="blue.700">
            Connect Your Calendar
          </Heading>
          <Text color="gray.700" mb={6} fontSize="md">
            To get started with Muro and see your events, please connect your calendar through the integrations tab.
          </Text>
          <Link href={integrations_path()}>
            <Button colorScheme="blue" size="lg">
              Go to Integrations
            </Button>
          </Link>
        </Box>
      )}

      {has_integrations && selectedEvents.length === 0 && (
        <Box textAlign="center" py={12} borderRadius="lg">
          <Text color="gray.500">No events scheduled for {selectedLabel.toLowerCase()}</Text>
        </Box>
      )}
      {selectedEvents.length > 0 && (
        <VStack maxW="3xl" w="full" mx="auto" alignItems="center">
          {selectedEvents.map((item, index) => (
            <Flex
              key={item.id}
              w="full"
              alignItems="center"
              justifyContent="space-between"
              p={4}
              borderRadius="lg"
              borderWidth="1px"
              shadow="sm"
              bg={item.provider === "muro" ? "green.50" : "white"}
              borderColor={item.provider === "muro" ? "green.200" : "primary-subtle"}
              _hover={{
                shadow: item.provider === "muro" ? "md" : "md",
              }}
              transition="box-shadow 0.2s"
              minH={`${getDurationMinutes(item.start_time, item.end_time) * PIXELS_PER_MINUTE}px`}
              mt={
                index === 0 ? "5px" : `${calculateGapPixels(selectedEvents[index - 1].end_time!, item.start_time!)}px`
              }
            >
              <Box flex="1" minW="0">
                <Text fontSize="sm" fontWeight="medium" color={item.provider === "muro" ? "green.700" : "primary-bold"}>
                  {formatTimeRange(item.start_time, item.end_time)}
                </Text>
              </Box>

              <Box flex="1" px={4} minW="0">
                <Text fontSize="sm" truncate color={item.provider === "muro" ? "green.800" : "gray.700"}>
                  {item.summary || "Untitled Event"}
                </Text>
              </Box>

              <Box flexShrink="0">
                <Text fontSize="sm" color={item.provider === "muro" ? "green.600" : "gray.500"}>
                  {calculateDuration(item.start_time, item.end_time)}
                </Text>
              </Box>

              {item.provider === "muro" && current_user?.is_super_user && (
                <IconButton
                  aria-label="Remove focus event"
                  size="sm"
                  variant="ghost"
                  colorPalette="red"
                  onClick={() => handleDeleteEvent(item.id)}
                  ml={2}
                >
                  <LuX />
                </IconButton>
              )}
            </Flex>
          ))}
        </VStack>
      )}
    </Box>
  );
};

Index.layout = (page: ReactNode) => <AppLayout>{page}</AppLayout>;

export default Index;
